import React, { useState } from 'react';
import { EvidenceRequirements } from '../common/BulletList';
import { entryMatchesUnit } from '../../lib/helpers';

/**
 * Where every unit stands, at a glance: Knowledge, Evidence, Workplace and
 * whether the unit is finished, one row per unit and broken down by year.
 * Opening a row shows what that unit's evidence has to demonstrate, so the same
 * table answers "what's outstanding?" and "what do I go and photograph?".
 */

const TICK = <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓</span>;
const DASH = <span style={{ color: 'var(--muted)' }}>—</span>;

function Cell({ children, title }) {
  return (
    <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontSize: 12 }} title={title}>
      {children}
    </td>
  );
}

function UnitRow({ unit, stats, canLog, onAddEvidence }) {
  const [open, setOpen] = useState(false);
  const hasGuide = unit.evidence_requirements || unit.photo_guide || unit.evidence_count_tip;
  const remaining = Math.max(0, stats.minEv - stats.approved);

  return (
    <>
      <tr onClick={() => setOpen(!open)} style={{ cursor: 'pointer', background: stats.complete ? 'rgba(39,174,96,.05)' : undefined }}>
        <td>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ color: 'var(--muted)', fontSize: 10 }}>{open ? '▾' : '▸'}</span>
            <div>
              <div style={{ fontFamily: 'var(--font-d)', fontWeight: 700, fontSize: 11.5, letterSpacing: .5, color: 'var(--accent)' }}>
                {unit.code}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text)', lineHeight: 1.4 }}>{unit.name}</div>
            </div>
          </div>
        </td>

        <Cell title={stats.knowledge ? 'Knowledge signed off' : 'Knowledge not ticked yet'}>
          {stats.knowledge ? TICK : DASH}
        </Cell>

        <Cell title={`${stats.approved} of ${stats.minEv} approved entries`}>
          {stats.evidenceMet ? TICK : (
            <span style={{ color: stats.approved > 0 ? 'var(--accent)' : 'var(--muted)', fontWeight: 600 }}>
              {stats.approved}/{stats.minEv}
            </span>
          )}
          {stats.pending > 0 && (
            <div style={{ fontSize: 9.5, color: 'var(--muted)' }}>{stats.pending} waiting</div>
          )}
          {stats.rejected > 0 && (
            <div style={{ fontSize: 9.5, color: 'var(--red)' }}>{stats.rejected} to fix</div>
          )}
        </Cell>

        <Cell title={stats.workplace ? 'Workplace signed off' : 'Not workplace assessed yet'}>
          {stats.workplace ? TICK : DASH}
        </Cell>

        <Cell title={stats.complete ? 'Unit complete' : 'Still outstanding'}>
          {stats.complete
            ? <span className="badge approved" style={{ fontSize: 9.5 }}>Complete</span>
            : DASH}
        </Cell>
      </tr>

      {open && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--bg)', padding: '10px 14px 16px 30px' }}>
            {!stats.complete && (
              <div style={{ padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 10,
                background: remaining > 0 ? 'rgba(245,166,35,.08)' : 'rgba(58,123,213,.08)',
                border: remaining > 0 ? '1px solid rgba(245,166,35,.25)' : '1px solid rgba(58,123,213,.25)' }}>
                {remaining > 0 ? (
                  <><strong>{remaining} more {remaining === 1 ? 'entry' : 'entries'} needed</strong>
                    {stats.pending > 0 && <span> ({stats.pending} already waiting on the trainer)</span>}.</>
                ) : (
                  <>Evidence is done. Still needs{' '}
                    {[!stats.knowledge && 'the knowledge tick', !stats.workplace && 'workplace sign-off']
                      .filter(Boolean).join(' and ')} to close out.</>
                )}
              </div>
            )}

            {unit.evidence_requirements && (
              <>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--head)', marginBottom: 4, letterSpacing: .4 }}>
                  WHAT THIS UNIT NEEDS
                </div>
                <EvidenceRequirements text={unit.evidence_requirements} />
              </>
            )}

            {unit.photo_guide && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--head)', marginBottom: 4, letterSpacing: .4 }}>
                  📸 PHOTOS TO TAKE
                </div>
                <div style={{ fontSize: 11.5, lineHeight: 1.7, color: 'var(--text)' }}>
                  {unit.photo_guide.split(',').map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6 }}>
                      <span style={{ color: 'var(--accent)', flexShrink: 0 }}>📷</span>
                      <span>{item.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {unit.evidence_count_tip && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 }}>
                💡 {unit.evidence_count_tip}
              </div>
            )}

            {!hasGuide && (
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                No written guidance loaded for this unit yet — ask your trainer what they want to see.
              </div>
            )}

            {canLog && (
              <button className="btn xs" style={{ marginTop: 12 }} onClick={e => { e.stopPropagation(); onAddEvidence(unit.code); }}>
                + Log evidence for {unit.code}
              </button>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * One year, collapsed to a summary line until opened. Three years of units at
 * once is a lot of table to scroll past when you only care about the current one.
 */
function LevelCard({ level, rows, canLog, onAddEvidence }) {
  const [open, setOpen] = useState(false);

  const total = rows.length;
  const done = rows.filter(r => r.stats.complete).length;
  const needEvidence = rows.filter(r => !r.stats.evidenceMet).length;
  const waiting = rows.reduce((n, r) => n + r.stats.pending, 0);
  const toFix = rows.reduce((n, r) => n + r.stats.rejected, 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div
        className="card-hdr"
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', gap: 12, flexWrap: 'wrap' }}
      >
        <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--muted)', fontSize: 11 }}>{open ? '▾' : '▸'}</span>
          {level.label}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {waiting > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'rgba(245,166,35,.14)', color: 'var(--accent)' }}>
              {waiting} waiting
            </span>
          )}
          {toFix > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'rgba(192,57,43,.14)', color: 'var(--red)' }}>
              {toFix} to fix
            </span>
          )}
          {needEvidence > 0 && (
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{needEvidence} still need evidence</span>
          )}
          <div style={{ width: 90 }}>
            <div className="pbar-wrap">
              <div className="pbar acc" style={{ width: `${pct}%`, background: pct === 100 ? 'var(--green)' : undefined }} />
            </div>
          </div>
          <span style={{
            fontFamily: 'var(--font-d)', fontWeight: 900, fontSize: 14, minWidth: 46, textAlign: 'right',
            color: pct === 100 ? 'var(--green)' : 'var(--head)',
          }}>
            {done}/{total}
          </span>
        </div>
      </div>

      {open && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 180 }}>Unit</th>
                <th style={{ textAlign: 'center' }}>Knowledge</th>
                <th style={{ textAlign: 'center' }}>Evidence</th>
                <th style={{ textAlign: 'center' }}>Workplace</th>
                <th style={{ textAlign: 'center' }}>Unit complete</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ unit, stats }) => (
                <UnitRow key={unit.code} unit={unit} stats={stats} canLog={canLog} onAddEvidence={onAddEvidence} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ApprenticeUnitSummary({
  levels, apprenticeId, evidence, signoffs, workplaceSignoffs, knowledgeUnits,
  onAddEvidence, canLog = false,
}) {
  const mine = (evidence || []).filter(e => e.apprentice_id === apprenticeId);

  function statsFor(unit) {
    const forUnit = mine.filter(e => entryMatchesUnit(e, unit.code));
    const minEv = unit.min_evidence || 3;
    const approved = forUnit.filter(e => e.status === 'approved').length;
    const pending = forUnit.filter(e => e.status === 'pending').length;
    const rejected = forUnit.filter(e => e.status === 'rejected').length;
    const knowledge = (knowledgeUnits || []).some(k => k.apprentice_id === apprenticeId && k.unit_code === unit.code);
    const workplace = (workplaceSignoffs || []).some(s => s.apprentice_id === apprenticeId && s.unit_code === unit.code);
    // A trainer's evidence sign-off also counts as met — that's how evidence
    // gathered before this app existed gets recognised.
    const signedOff = (signoffs || []).some(s => s.apprentice_id === apprenticeId && s.unit_code === unit.code);
    const evidenceMet = signedOff || approved >= minEv;
    return {
      minEv, approved, pending, rejected, knowledge, workplace, evidenceMet,
      complete: knowledge && evidenceMet && workplace,
    };
  }

  const withUnits = levels.filter(l => l.units.length > 0);
  if (withUnits.length === 0) return null;

  return (
    <>
      {withUnits.map(level => {
        const units = [...level.units].sort((a, b) => a.code.localeCompare(b.code));
        const rows = units.map(u => ({ unit: u, stats: statsFor(u) }));
        return (
          <LevelCard
            key={level.year}
            level={level}
            rows={rows}
            canLog={canLog}
            onAddEvidence={onAddEvidence}
          />
        );
      })}
    </>
  );
}
